import {
  Box,
  Drawer,
  ActionIcon,
  Group,
  Stack,
  Loader,
  ScrollArea,
  Text,
  Image,
} from "@mantine/core";
import { IconMenu2 } from "@tabler/icons-react";
import Navigation from "../Navigation/Navigation";
import { UserButton } from "../components/UserButton";
import BrandLogoName from "../components/BrandLogoName";
import GlobalSearch from "../components/GlobalSearch";
import { useLocalStorage } from "@mantine/hooks";
import { fetchMyProfile } from "../api/authentication";
import { TokenType } from "../api/api-service";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import logo from "../images/think_cloudly_logo_transparent.png";
import { IconX } from "@tabler/icons-react";
import { isCurrentUserBatchOnly } from "../auth/batchOnlyAccess";


type AuthenticationState = "authenticated" | "unauthenticated" | "loading";

// Batch-only accounts (REACT_APP_BATCH_ONLY_EMAILS) may only reach the All
// Batches module — everything else redirects back, so hiding the nav item
// isn't the only thing stopping them (a typed URL would otherwise bypass it).
const BATCH_ONLY_ALLOWED_PATHS = [/^\/batches$/i, /^\/batches\/[^/]+\/journey$/i];

export function Layout() {
  const [token] = useLocalStorage<TokenType>({
    key: "token",
    getInitialValueInEffect: false,
  });
  const [authenticationState, setAuthenticationState] =
    useState<AuthenticationState>(token ? "loading" : "unauthenticated");
  const [mobileNavOpened, setMobileNavOpened] = useState(false);
  const location = useLocation();

  async function checkAuth() {
    if (token) {
      const response = await fetchMyProfile();
      if (response instanceof Error) {
        localStorage.clear();
        setAuthenticationState("unauthenticated");
      } else {
        setAuthenticationState(
          response.data ? "authenticated" : "unauthenticated",
        );
      }
    } else {
      setAuthenticationState("unauthenticated");
    }
  }

  useEffect(() => {
    checkAuth();
  }, [token]);

  // Loading State
  if (authenticationState === "loading") {
    return (
      <Stack
        h="100vh"
        align="center"
        justify="center"
        className="bg-gradient-to-br from-gray-50 to-gray-100"
      >
        <Loader size="lg" color="gray" />
      </Stack>
    );
  }

  // Unauthenticated State
  if (authenticationState === "unauthenticated") {
    return <Navigate to="/login" />;
  }

  if (
    isCurrentUserBatchOnly() &&
    !BATCH_ONLY_ALLOWED_PATHS.some((re) => re.test(location.pathname))
  ) {
    return <Navigate to="/Batches" replace />;
  }

  // Authenticated State - Trainer Portal Style Layout
  return (
    <Box
      style={{
        display: "flex",
        height: "100vh",
        width: "100%",
        backgroundColor: "#ffffff",
      }}
    >
      {/* Desktop Sidebar - Fixed Left */}

      {/* Mobile Navigation Drawer */}
      <Drawer
        opened={mobileNavOpened}
        onClose={() => setMobileNavOpened(false)}
        padding="sm"
        size={260}
        withCloseButton={false}
        classNames={{
          content: "bg-white",
        }}
        overlayProps={{ backgroundOpacity: 0.5, blur: 4 }}
      >
      {/* Drawer Header */}
<Box
  mb="sm"
  style={{
    paddingBottom: "12px",
    borderBottom: "1px solid #e5e7eb",
  }}
>
  {/* TOP ROW */}
  <Group justify="space-between" align="center">
    <Group gap="xs" align="center">
      <Image
        src={logo}
        alt="Thinkcloudly Logo"
        width={36}
        height={36}
        fit="contain"
        className="h-[36px]"
      />
      <Text fw={600} size="sm" style={{ color: "#1f2937" }}>
        Thinkcloudly
      </Text>
    </Group>

    {/* CLOSE ICON */}
    <ActionIcon
      variant="subtle"
      onClick={() => setMobileNavOpened(false)}
    >
      <IconX size={20} />
    </ActionIcon>
  </Group>

  {/* SECOND ROW */}
  {/* <Text size="xs" style={{ color: "#9ca3af", marginLeft: 44 }}>
    Admin Dashboard
  </Text> */}
</Box>


        {/* Search Bar (Mobile Only - Inside Drawer) */}
        <Box mb="sm" px="xs">
          <GlobalSearch />
        </Box>

        {/* Navigation Content */}
        <ScrollArea style={{ flex: 1 }}>
          <Navigation onLinkClick={() => setMobileNavOpened(false)} />
        </ScrollArea>

        {/* Footer */}
        <Box
          p="xs"
          style={{
            marginTop: "auto",
            borderTop: "1px solid #e5e7eb",
            backgroundColor: "#f9fafb",
          }}
        >
          <Stack gap={2} align="center">
            <Text size="xs" fw={600} style={{ color: "#1f2937" }}>
              Thinkcloudly
            </Text>
            <Text size="xs" style={{ color: "#9ca3af" }}>
              © {new Date().getFullYear()}
            </Text>
            <Text size="xs" style={{ color: "#9ca3af" }}>
              Version 1.3
            </Text>
          </Stack>
        </Box>
      </Drawer>

      {/* Main Content Area - Flex Column */}
      <Box
        style={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          overflow: "hidden",
        }}
      >
        {/* Top Header */}
        <Box
          style={{
            minHeight: "60px",
            backgroundColor: "#ffffff",
            borderBottom: "1px solid #e5e7eb",
            display: "flex",
            alignItems: "center",
            padding: "0.75rem 1rem",
            boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
            zIndex: 100,
          }}
        >
          {/* DESKTOP LAYOUT */}
<Group
  align="center"
  style={{ width: "100%" }}
  className="hidden md:flex"
>
  {/* LEFT - Hamburger + Search */}
  <Box style={{ width: 360, display: "flex", alignItems: "center", gap: 12 }}>
    <ActionIcon
      variant="subtle"
      size="lg"
      onClick={() => setMobileNavOpened(true)}
    >
      <IconMenu2 size={24} />
    </ActionIcon>

    <Box style={{ width: 280 }}>
      <GlobalSearch />
    </Box>
  </Box>

  {/* CENTER - Logo (true center) */}
  <Box style={{ flex: 1, textAlign: "center" }}>
    <BrandLogoName />
  </Box>

  {/* RIGHT - User (same width as LEFT) */}
  <Box
    style={{
      width: 360,
      display: "flex",
      justifyContent: "flex-end",
      alignItems: "center",
    }}
  >
    <UserButton />
  </Box>
</Group>




          {/* MOBILE LAYOUT */}
          <Group
  align="center"
  style={{ width: "100%" }}
  className="flex md:hidden"
>
  {/* LEFT - Hamburger */}
  <Box style={{ width: 40 }}>
    <ActionIcon
      variant="subtle"
      size="lg"
      onClick={() => setMobileNavOpened(true)}
    >
      <IconMenu2 size={24} />
    </ActionIcon>
  </Box>

  {/* CENTER - Logo */}
  <Box style={{ flex: 1, textAlign: "center", }}>
    <BrandLogoName />
  </Box>

  {/* RIGHT - User */}
  <Box style={{ width: 40, display: "flex", justifyContent: "flex-end" }}>
    <UserButton />
  </Box>
</Group>

        </Box>

        {/* Page Content - Scrollable */}
        <Box
          style={{
            flex: 1,
            overflow: "auto",
            backgroundColor: "#ffffff",
            padding: "1rem",
          }}
        >
          <Box style={{ maxWidth: "100%" }}>
            <Outlet />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
